/**
 * /s/:token — the page a file share link opens. A public link works without an account; an
 * organisation link needs the visitor to be signed in to Eurion in this browser.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, LogIn } from 'lucide-react';
import { api, API_BASE } from '../../lib/api';
import { tv } from '../../lib/i18nText';
import { intlLocale } from '../teams/i18nFallback';

interface LinkInfo { name: string; mimeType: string | null; sizeBytes: number; scope: 'org' | 'public'; expiresAt: string | null }
type State = { kind: 'loading' } | { kind: 'ok'; info: LinkInfo } | { kind: 'signin' } | { kind: 'gone' } | { kind: 'error' };

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function SharedLinkPage({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [downloading, setDownloading] = useState(false);
  const path = `/v1/files/links/${encodeURIComponent(token)}`;
  const signedIn = !!localStorage.getItem('eurion_token');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Signed in: the api helper refreshes an expired session. Not signed in: a plain request,
        // so a public link never sends the visitor to the login page.
        const res = signedIn
          ? await api.get<LinkInfo>(path)
          : await fetch(`${API_BASE}${path}`).then(async (r) => ({ status: r.status, ...(await r.json()) }));
        if (cancelled) return;
        const status = (res as { status?: number }).status;
        if (res.success && res.data) setState({ kind: 'ok', info: res.data as LinkInfo });
        else if (status === 401 || res.error?.code === 'AUTH_001') setState({ kind: 'signin' });
        else if (status === 404 || res.error?.code === 'RES_001') setState({ kind: 'gone' });
        else setState({ kind: 'error' });
      } catch {
        if (!cancelled) setState({ kind: 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, [path, signedIn]);

  const download = async (info: LinkInfo) => {
    if (info.scope === 'public') { window.location.href = `${API_BASE}${path}/download`; return; }
    // Organisation links need the session's token, so fetch and save
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}${path}/download`, { headers: { Authorization: `Bearer ${localStorage.getItem('eurion_token') ?? ''}` } });
      if (!res.ok) { setState({ kind: res.status === 401 ? 'signin' : 'gone' }); return; }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = info.name; a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <main className="min-h-screen bg-nord-0 flex items-center justify-center p-4">
      <div className="card w-full max-w-md p-6 text-center">
        <div className="mb-5 flex items-center justify-center gap-2">
          <img src="/logo-mark.svg" alt="" aria-hidden="true" className="h-7 w-7" />
          <span className="font-semibold text-nord-6">Eurion</span>
        </div>
        {state.kind === 'loading' && (
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-nord-8/30 border-t-nord-8" role="status" aria-label={t('common.loading', 'Loading…')} />
        )}
        {state.kind === 'ok' && (
          <>
            <FileText className="mx-auto mb-3 h-10 w-10 text-nord-8" aria-hidden="true" />
            <h1 className="break-words text-lg font-semibold text-nord-6">{state.info.name}</h1>
            <p className="mt-1 text-sm text-nord-muted">
              {formatBytes(state.info.sizeBytes)}
              {state.info.expiresAt && ` · ${tv(t, 'filesUi.share.expires', 'Expires {{date}}', { date: new Date(state.info.expiresAt).toLocaleDateString(intlLocale(i18n?.language)) })}`}
            </p>
            <button type="button" onClick={() => void download(state.info)} disabled={downloading}
              className="btn-primary mx-auto mt-5 flex items-center gap-2 disabled:opacity-60">
              <Download className="h-4 w-4" aria-hidden="true" /> {t('files.download', 'Download')}
            </button>
            <p className="mt-4 text-xs text-nord-muted">{t('filesUi.share.sharedViaEurion', 'Shared with you via Eurion.')}</p>
          </>
        )}
        {state.kind === 'signin' && (
          <>
            <LogIn className="mx-auto mb-3 h-10 w-10 text-nord-8" aria-hidden="true" />
            <h1 className="text-lg font-semibold text-nord-6">{t('filesUi.share.signInTitle', 'Sign in to open this file')}</h1>
            <p className="mt-1 text-sm text-nord-muted">{t('filesUi.share.signInHint', 'This link is for people in the organisation. Sign in to Eurion, then open the link again.')}</p>
            <a href="/" className="btn-primary mx-auto mt-5 inline-flex items-center gap-2">
              <LogIn className="h-4 w-4" aria-hidden="true" /> {t('auth.signIn', 'Sign in')}
            </a>
          </>
        )}
        {(state.kind === 'gone' || state.kind === 'error') && (
          <>
            <h1 className="text-lg font-semibold text-nord-6">
              {state.kind === 'gone' ? t('filesUi.share.goneTitle', 'This link does not work') : t('filesUi.share.errorTitle', 'Something went wrong')}
            </h1>
            <p className="mt-1 text-sm text-nord-muted">
              {state.kind === 'gone'
                ? t('filesUi.share.goneHint', 'It may have expired or been turned off. Ask the person who shared it for a new link.')
                : t('filesUi.share.errorHint', 'Try again in a moment.')}
            </p>
          </>
        )}
      </div>
    </main>
  );
}
